import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTickerAutocomplete } from '../hooks/useTickerAutocomplete'; // TICKER-SEARCH-AUTOCOMPLETE
import { Activity, TrendingUp, TrendingDown, RefreshCw, Zap, Crown, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// ── Helpers ───────────────────────────────────────────────────────────────────
const sigColor = s => s === 'BULLISH' ? '#00ff88' : s === 'BEARISH' ? '#ff4444' : '#ffaa00';
const sigIcon  = s => s === 'BULLISH' ? '▲' : s === 'BEARISH' ? '▼' : '◆';
const pctColor = v => v >= 0 ? '#00ff88' : '#ff4444';
const fmtPct   = v => (v >= 0 ? '+' : '') + Number(v).toFixed(2) + '%';
const fmtPrice = v => v ? '$' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';

// ── Market Status ─────────────────────────────────────────────────────────────
function getMarketStatus() {
  const now = new Date();
  const et  = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
  const day = et.getDay();
  const mins = et.getHours() * 60 + et.getMinutes();
  if (day === 0 || day === 6) return { status: 'CLOSED', label: 'Weekend',      color: '#64748b' };
  if (mins >= 570 && mins < 960)  return { status: 'OPEN',  label: 'Market Open',  color: '#00ff88' };
  if (mins >= 240 && mins < 570)  return { status: 'PRE',   label: 'Pre-Market',   color: '#ffaa00' };
  if (mins >= 960 && mins < 1200) return { status: 'AFTER', label: 'After-Hours',  color: '#ffaa00' };
  return { status: 'CLOSED', label: 'Market Closed', color: '#64748b' };
}

// ── Inline Sparkline (last 14 closes) ────────────────────────────────────────
function Sparkline({ candles, color = '#00ff88' }) {
  if (!candles || candles.length < 2) return null;
  const prices = candles.slice(-14).map(c => c.close || c.adjClose || 0);
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const range = max - min || 1;
  const W = 80, H = 28;
  const pts = prices.map((p, i) => {
    const x = (i / (prices.length - 1)) * W;
    const y = H - ((p - min) / range) * H;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg width={W} height={H} style={{ display: 'block' }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5"
        strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ── Hover Preview Card ────────────────────────────────────────────────────────
function HoverCard({ ticker, signal, onNavigate, onAnalyze, style }) {
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/seo/${ticker}/chart?resolution=D`)
      .then(r => r.json())
      .then(d => { if (!cancelled && d.candles) setDetail(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [ticker]);

  const candles = detail?.candles || [];
  const isUp = signal?.changePercent >= 0;

  return (
    <div style={{
      position: 'absolute', left: '100%', top: '50%', transform: 'translateY(-50%)',
      marginLeft: 12, zIndex: 200, width: 240,
      background: 'rgba(8,16,36,0.98)', border: '1px solid rgba(0,212,255,0.18)',
      borderRadius: 14, padding: '14px 16px', boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
      backdropFilter: 'blur(20px)', ...style,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: '0.95rem', fontWeight: 900, color: '#f8fafc' }}>{ticker}</div>
          <div style={{ fontSize: '0.7rem', color: sigColor(signal?.signal), fontWeight: 700, marginTop: 2 }}>
            {sigIcon(signal?.signal)} {signal?.signal || '—'}
            <span style={{ color: '#475569', fontWeight: 400, marginLeft: 6 }}>{signal?.score}/100</span>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '0.9rem', fontWeight: 800, color: '#f8fafc' }}>{fmtPrice(signal?.price)}</div>
          {signal?.changePercent != null && (
            <div style={{ fontSize: '0.72rem', color: pctColor(signal.changePercent), fontWeight: 600 }}>
              {fmtPct(signal.changePercent)}
            </div>
          )}
        </div>
      </div>

      {/* Sparkline */}
      <div style={{ marginBottom: 10, height: 28 }}>
        {candles.length > 1
          ? <Sparkline candles={candles} color={isUp ? '#00ff88' : '#ff4444'} />
          : <div style={{ fontSize: '0.65rem', color: '#334155' }}>Loading chart…</div>
        }
      </div>

      {/* Quick stats */}
      {signal && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 12 }}>
          {[
            { label: 'Score',    value: `${signal.score}/100` },
            { label: 'Signal',   value: signal.signal || '—', color: sigColor(signal.signal) },
          ].map(item => (
            <div key={item.label} style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 8, padding: '6px 8px' }}>
              <div style={{ fontSize: '0.6rem', color: '#475569', marginBottom: 2 }}>{item.label}</div>
              <div style={{ fontSize: '0.78rem', fontWeight: 700, color: item.color || '#f8fafc' }}>{item.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 6 }}>
        <button onClick={() => onAnalyze(ticker)}
          style={{ flex: 1, padding: '6px 0', background: 'linear-gradient(135deg,#00ff88,#00d4ff)', border: 'none', borderRadius: 8, color: '#050d1a', fontSize: '0.75rem', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit' }}>
          <Zap size={11} style={{ marginRight: 4, verticalAlign: 'middle' }} />Run AI
        </button>
        <button onClick={() => onNavigate('stock', { ticker })}
          style={{ flex: 1, padding: '6px 0', background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.2)', borderRadius: 8, color: '#00d4ff', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
          Full Page →
        </button>
      </div>
    </div>
  );
}

// ── Watchlist Row (with hover) ────────────────────────────────────────────────
function WatchlistRow({ ticker, signal, onNavigate, onAnalyze, onRemove, loading }) {
  const [hovered, setHovered] = useState(false);
  const hoverTimer = useRef(null);
  const rowRef = useRef(null);

  const handleMouseEnter = () => {
    hoverTimer.current = setTimeout(() => setHovered(true), 350);
  };
  const handleMouseLeave = () => {
    clearTimeout(hoverTimer.current);
    setHovered(false);
  };

  return (
    <div ref={rowRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}
      style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '0.75rem',
        padding: '0.65rem 0.85rem', background: hovered ? 'rgba(0,212,255,0.05)' : 'rgba(255,255,255,0.02)',
        border: `1px solid ${hovered ? 'rgba(0,212,255,0.2)' : 'rgba(255,255,255,0.05)'}`,
        borderRadius: 10, transition: 'all 0.15s', cursor: 'default' }}>

      {/* Ticker */}
      <button onClick={() => onNavigate('stock', { ticker })}
        style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 900,
          fontSize: '0.95rem', color: '#f8fafc', fontFamily: 'inherit', padding: 0,
          minWidth: 56, textAlign: 'left', letterSpacing: '0.02em' }}>
        {ticker}
      </button>

      {/* Signal */}
      {signal ? (
        <>
          <span style={{ fontSize: '0.78rem', fontWeight: 700, color: sigColor(signal.signal), minWidth: 90 }}>
            {sigIcon(signal.signal)} {signal.signal}
          </span>
          <span style={{ fontSize: '0.72rem', color: '#475569', minWidth: 44 }}>{signal.score}/100</span>
          <span style={{ fontSize: '0.82rem', color: '#94a3b8', marginLeft: 'auto' }}>{fmtPrice(signal.price)}</span>
          {!signal.marketClosed && signal.changePercent != null && (
            <span style={{ fontSize: '0.72rem', fontWeight: 600, color: pctColor(signal.changePercent), minWidth: 56, textAlign: 'right' }}>
              {fmtPct(signal.changePercent)}
            </span>
          )}
        </>
      ) : loading ? (
        <span style={{ fontSize: '0.72rem', color: '#334155', marginLeft: 'auto' }}>Loading…</span>
      ) : (
        <span style={{ fontSize: '0.72rem', color: '#334155', marginLeft: 'auto' }}>—</span>
      )}

      {/* Remove */}
      <button onClick={() => onRemove(ticker)}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#334155',
          fontSize: '0.8rem', padding: '0 0 0 4px', fontFamily: 'inherit', lineHeight: 1,
          flexShrink: 0 }}
        onMouseEnter={e => e.currentTarget.style.color = '#ff4444'}
        onMouseLeave={e => e.currentTarget.style.color = '#334155'}
        title="Remove">✕</button>

      {/* Hover preview card */}
      {hovered && signal && (
        <HoverCard ticker={ticker} signal={signal} onNavigate={onNavigate} onAnalyze={onAnalyze} />
      )}
    </div>
  );
}

// ── Intelligence Mini Strip ───────────────────────────────────────────────────
function IntelStrip({ intel }) {
  if (!intel) return null;
  const { fearGreed, vix } = intel;
  const fgColor = fearGreed?.score >= 60 ? '#00ff88' : fearGreed?.score >= 40 ? '#ffaa00' : '#ff4444';
  const vixColor = vix?.label === 'Low' ? '#00ff88' : vix?.label === 'Normal' ? '#64748b' : vix?.label === 'Elevated' ? '#ffaa00' : '#ef4444';

  return (
    <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
      {fearGreed && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0.85rem',
          background: `${fgColor}10`, border: `1px solid ${fgColor}25`, borderRadius: 20 }}>
          <span style={{ fontSize: '0.65rem', color: '#475569', fontWeight: 700 }}>FEAR & GREED</span>
          <span style={{ fontSize: '0.8rem', fontWeight: 900, color: fgColor }}>{fearGreed.score}</span>
          <span style={{ fontSize: '0.65rem', color: fgColor }}>{fearGreed.label}</span>
        </div>
      )}
      {vix && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0.85rem',
          background: `${vixColor}10`, border: `1px solid ${vixColor}25`, borderRadius: 20 }}>
          <span style={{ fontSize: '0.65rem', color: '#475569', fontWeight: 700 }}>VIX</span>
          <span style={{ fontSize: '0.8rem', fontWeight: 900, color: vixColor }}>{vix.price}</span>
          <span style={{ fontSize: '0.65rem', color: vixColor }}>{vix.label}</span>
        </div>
      )}
      <button onClick={() => window.location.href = '#market'}
        style={{ padding: '0.4rem 0.85rem', background: 'rgba(0,212,255,0.06)',
          border: '1px solid rgba(0,212,255,0.15)', borderRadius: 20,
          color: '#00d4ff', fontSize: '0.65rem', fontWeight: 700, cursor: 'pointer',
          fontFamily: 'inherit' }}>
        Full Intelligence →
      </button>
    </div>
  );
}

// ── Movers Tab Panel ──────────────────────────────────────────────────────────
function MoversPanel({ gainers, losers, active, onNavigate }) {
  const [tab, setTab] = useState('gainers');
  const lists = { gainers, losers, active };
  const current = (lists[tab] || []).slice(0, 6);
  const tabs = [
    { id: 'gainers', label: '🔥 Gainers', color: '#00ff88' },
    { id: 'losers',  label: '📉 Losers',  color: '#ff4444' },
    { id: 'active',  label: '📊 Active',  color: '#00d4ff' },
  ];

  return (
    <div>
      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ padding: '0.35rem 0.85rem', borderRadius: 20, cursor: 'pointer',
              background: tab === t.id ? `${t.color}15` : 'transparent',
              border: tab === t.id ? `1px solid ${t.color}35` : '1px solid transparent',
              color: tab === t.id ? t.color : '#475569',
              fontSize: '0.72rem', fontWeight: 700, fontFamily: 'inherit', transition: 'all 0.15s' }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {current.map(stock => {
          const pct = stock.changePercent ?? stock.change_percent ?? 0;
          const isUp = pct >= 0;
          return (
            <div key={stock.ticker} onClick={() => onNavigate('stock', { ticker: stock.ticker })}
              style={{ display: 'flex', alignItems: 'center', gap: '0.75rem',
                padding: '0.5rem 0.75rem', borderRadius: 8, cursor: 'pointer',
                background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)',
                transition: 'all 0.12s' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,212,255,0.04)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}>
              <span style={{ fontWeight: 900, fontSize: '0.9rem', color: '#f8fafc', minWidth: 56 }}>{stock.ticker}</span>
              <span style={{ fontSize: '0.78rem', color: '#64748b', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{stock.name}</span>
              <span style={{ fontSize: '0.82rem', color: '#94a3b8' }}>${Number(stock.price || 0).toFixed(2)}</span>
              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: isUp ? '#00ff88' : '#ff4444', minWidth: 56, textAlign: 'right' }}>
                {isUp ? <TrendingUp size={11} style={{ marginRight: 2, verticalAlign: 'middle' }} /> : <TrendingDown size={11} style={{ marginRight: 2, verticalAlign: 'middle' }} />}
                {isUp ? '+' : ''}{Number(pct).toFixed(2)}%
              </span>
            </div>
          );
        })}
        {current.length === 0 && (
          <div style={{ padding: '1rem', textAlign: 'center', color: '#334155', fontSize: '0.82rem' }}>
            Loading market data…
          </div>
        )}
      </div>
    </div>
  );
}

// ── Super Admin D3 Status Panel (placeholder — Phase D fills this) ────────────
function D3StatusPanel() {
  return (
    <div style={{ padding: '1.25rem 1.5rem', background: 'rgba(123,47,255,0.05)',
      border: '1px solid rgba(123,47,255,0.2)', borderRadius: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <Crown size={14} style={{ color: '#7b2fff' }} />
        <span style={{ fontSize: '0.72rem', fontWeight: 800, color: '#7b2fff', letterSpacing: '0.08em' }}>
          D3 ENGINE STATUS
        </span>
        <span style={{ fontSize: '0.62rem', padding: '0.1rem 0.5rem', background: 'rgba(255,170,0,0.12)',
          border: '1px solid rgba(255,170,0,0.3)', borderRadius: 10, color: '#ffaa00', fontWeight: 700 }}>
          PHASE D — NOT ACTIVE
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8 }}>
        {[
          { label: 'OCI Server',       status: 'BLOCKED', color: '#ff4444', note: 'Oracle ticket open' },
          { label: 'IB Gateway',       status: 'WAITING', color: '#ffaa00', note: 'Needs OCI first' },
          { label: 'D3 Phase',         status: 'D0 — MANUAL', color: '#64748b', note: '60d trading journal' },
          { label: 'Emergency Stop',   status: 'NOT BUILT', color: '#ff4444', note: 'Required before D3c' },
        ].map(item => (
          <div key={item.label} style={{ background: 'rgba(255,255,255,0.02)', borderRadius: 8, padding: '0.6rem 0.75rem' }}>
            <div style={{ fontSize: '0.6rem', color: '#475569', marginBottom: 3 }}>{item.label}</div>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: item.color }}>{item.status}</div>
            <div style={{ fontSize: '0.6rem', color: '#334155', marginTop: 2 }}>{item.note}</div>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 10, fontSize: '0.68rem', color: '#475569' }}>
        <AlertTriangle size={10} style={{ color: '#ffaa00', marginRight: 4, verticalAlign: 'middle' }} />
        D3 engine requires OCI VM + IB Gateway + 60 days manual trading log. Contact Oracle Sales to unblock.
      </div>
    </div>
  );
}

// ── Main Dashboard ────────────────────────────────────────────────────────────
export default function DashboardPage({
  user, token, onNavigate, onAnalyze,
  marketData, topGainers, topLosers, mostActive, refresh, moversSource,
  isSuperAdmin,
}) {
  const [now,          setNow]          = useState(new Date());
  const [tickers,      setTickers]      = useState([]);
  const [signals,      setSignals]      = useState({});
  const [loadingSig,   setLoadingSig]   = useState(false);
  const [digestOn,     setDigestOn]     = useState(user?.digestOptIn || false);
  const [digestSaving, setDigestSaving] = useState(false);
  const [addInput,     setAddInput]     = useState('');
  const [addDropdownOpen, setAddDropdownOpen] = useState(false); // TICKER-SEARCH-AUTOCOMPLETE
  const addBoxRef = useRef(null);
  const { suggestions: addSuggestions } = useTickerAutocomplete(addInput);
  const [adding,       setAdding]       = useState(false);
  const [addError,     setAddError]     = useState('');
  const [intel,        setIntel]        = useState(null);
  const marketStatus = getMarketStatus();

  // Live clock
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);

  // Market intelligence
  useEffect(() => {
    fetch(API_URL + '/api/market/intelligence')
      .then(r => r.json())
      .then(d => { if (d.success && d.data) setIntel(d.data); })
      .catch(() => {});
  }, []);

  // Load watchlist
  const loadWatchlist = useCallback(async () => {
    if (!token) return;
    try {
      const res  = await fetch(API_URL + '/api/watchlist', { headers: { Authorization: 'Bearer ' + token } });
      const data = await res.json();
      if (data.success) setTickers(data.tickers || []);
    } catch (e) {}
  }, [token]);

  // Load signals for watchlist
  const loadSignals = useCallback(async (tickerList) => {
    if (!tickerList.length) return;
    setLoadingSig(true);
    const results = {};
    await Promise.all(tickerList.map(async (ticker) => {
      try {
        const res  = await fetch(`${API_URL}/api/seo/stocks/${ticker}`);
        const data = await res.json();
        if (data.success) results[ticker] = {
          signal: data.signal, score: data.score,
          price: data.quote?.price || data.quote?.prevClose,
          changePercent: data.quote?.changePercent || 0,
          marketClosed: !data.quote?.price || data.quote?.price === 0,
        };
      } catch (e) {}
    }));
    setSignals(results);
    setLoadingSig(false);
  }, []);

  useEffect(() => { loadWatchlist(); }, [loadWatchlist]);
  useEffect(() => { if (tickers.length) loadSignals(tickers); }, [tickers, loadSignals]);

  useEffect(() => { // TICKER-SEARCH-AUTOCOMPLETE
    const handler = (e) => { if (addBoxRef.current && !addBoxRef.current.contains(e.target)) setAddDropdownOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const removeTicker = async (ticker) => {
    try {
      await fetch(`${API_URL}/api/watchlist/${ticker}`, {
        method: 'DELETE', headers: { Authorization: 'Bearer ' + token },
      });
      setTickers(prev => prev.filter(t => t !== ticker));
    } catch (e) {}
  };

  const addTicker = async (overrideTicker) => {
    // TICKER-SEARCH-AUTOCOMPLETE: accepts an optional direct ticker (from the
    // new dropdown's onSelect) — same typeof guard already used for handleAnalyze
    // elsewhere in this codebase, since a plain onClick={addTicker} still passes
    // a MouseEvent as the first arg (not a string), which must keep falling
    // through to addInput exactly as before.
    const t = (typeof overrideTicker === 'string' ? overrideTicker : addInput).toUpperCase().trim();
    if (!t) return;
    if (tickers.includes(t)) { setAddError('Already in watchlist'); return; }
    if (tickers.length >= 10) { setAddError('Max 10 tickers'); return; }
    setAdding(true); setAddError('');
    try {
      const res  = await fetch(`${API_URL}/api/watchlist`, {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ticker: t }),
      });
      const data = await res.json();
      if (data.success) {
        setTickers(data.tickers || [...tickers, t]);
        setAddInput('');
      } else {
        setAddError(data.message || 'Could not add ticker');
      }
    } catch (e) { setAddError('Network error'); }
    setAdding(false);
  };

  const toggleDigest = async () => {
    setDigestSaving(true);
    try {
      const res  = await fetch(`${API_URL}/api/watchlist/digest`, {
        method: 'PATCH',
        headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ optIn: !digestOn }),
      });
      const data = await res.json();
      if (data.success) setDigestOn(data.digestOptIn);
    } catch (e) {}
    setDigestSaving(false);
  };

  // ET + local time
  const etOpts    = { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', hour12: true };
  const etTime    = now.toLocaleTimeString('en-US', etOpts);
  const localTZ   = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const localOpts = { hour: '2-digit', minute: '2-digit', hour12: true, timeZoneName: 'short' };
  const localTime = now.toLocaleTimeString('en-US', localOpts);
  const isLocalET = localTZ === 'America/New_York' || localTZ.startsWith('America/New_Y');

  const et         = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
  const etMins     = et.getHours() * 60 + et.getMinutes();
  let nextEvent = '';
  if (marketStatus.status === 'OPEN')  nextEvent = `Closes in ${Math.floor((960 - etMins) / 60)}h ${(960 - etMins) % 60}m`;
  else if (marketStatus.status === 'PRE') nextEvent = `Opens in ${Math.floor((570 - etMins) / 60)}h ${(570 - etMins) % 60}m`;
  else nextEvent = et.getDay() === 5 || et.getDay() === 0 ? 'Opens Monday 9:30 AM ET' : 'Opens 9:30 AM ET';

  const C = {
    bg: '#050d1a', card: 'rgba(255,255,255,0.02)', border: 'rgba(255,255,255,0.06)',
    text: '#f8fafc', muted: '#64748b', subtle: '#334155',
  };

  return (
    <div style={{ padding: '0 1.5rem 4rem', fontFamily: 'system-ui, sans-serif' }}>

      {/* ── Market Clock Bar ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap',
        padding: '0.5rem 0.85rem', borderRadius: 10, marginBottom: '1.5rem',
        background: C.card, border: `1px solid ${C.border}` }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
          padding: '0.25rem 0.7rem', borderRadius: 20,
          background: marketStatus.color + '18', border: `1px solid ${marketStatus.color}40` }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: marketStatus.color,
            display: 'inline-block',
            boxShadow: marketStatus.status === 'OPEN' ? `0 0 5px ${marketStatus.color}` : 'none',
            animation: marketStatus.status === 'OPEN' ? 'pulse 2s infinite' : 'none' }} />
          <span style={{ fontSize: '0.7rem', fontWeight: 700, color: marketStatus.color, letterSpacing: '0.05em' }}>
            {marketStatus.label.toUpperCase()}
          </span>
        </div>
        <span style={{ fontSize: '0.72rem', color: '#64748b', fontVariantNumeric: 'tabular-nums' }}>
          {etTime} <span style={{ color: C.subtle }}>ET</span>
        </span>
        <span style={{ color: C.subtle, fontSize: '0.72rem' }}>·</span>
        {!isLocalET && (
          <>
            <span style={{ fontSize: '0.72rem', color: '#475569' }}>
              Your time: <span style={{ color: '#64748b', fontVariantNumeric: 'tabular-nums' }}>{localTime}</span>
            </span>
            <span style={{ color: C.subtle, fontSize: '0.72rem' }}>·</span>
          </>
        )}
        <span style={{ fontSize: '0.72rem', color: '#475569' }}>{nextEvent}</span>
        <span style={{ flex: 1 }} />
        <IntelStrip intel={intel} />
      </div>

      {/* ── Two-column grid ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.4fr) minmax(0,1fr)', gap: '1.25rem', alignItems: 'start' }}>

        {/* LEFT: Watchlist */}
        <div>
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: '1.25rem 1.5rem' }}>
            {/* Watchlist header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 800, color: '#64748b', letterSpacing: '0.08em' }}>
                ⭐ MY WATCHLIST
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: '0.68rem', color: '#334155' }}>{tickers.length}/10</span>
                <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
                  <input type="checkbox" checked={digestOn} onChange={toggleDigest}
                    disabled={digestSaving || tickers.length === 0}
                    style={{ accentColor: '#00d4ff', width: 12, height: 12 }} />
                  <span style={{ fontSize: '0.65rem', color: digestOn ? '#00d4ff' : '#334155', fontWeight: digestOn ? 700 : 400 }}>
                    {digestSaving ? 'Saving…' : '📧 Daily digest'}
                  </span>
                </label>
              </div>
            </div>

            {/* Hover tip */}
            <div style={{ fontSize: '0.62rem', color: '#1e293b', marginBottom: '0.75rem' }}>
              Hover a ticker for quick preview · Click ticker for full stock page
            </div>

            {/* Ticker rows */}
            {tickers.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: '1rem',
                maxHeight: 320, overflowY: tickers.length > 6 ? 'auto' : 'visible' }}>
                {tickers.map(ticker => (
                  <WatchlistRow key={ticker} ticker={ticker} signal={signals[ticker]}
                    onNavigate={onNavigate} onAnalyze={onAnalyze}
                    onRemove={removeTicker} loading={loadingSig} />
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '1.5rem 0', color: '#334155', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                No stocks saved yet. Browse a stock page and click <strong style={{ color: '#64748b' }}>+ Watchlist</strong>.
              </div>
            )}

            {/* Add ticker */}
            {tickers.length < 10 && (
              <div ref={addBoxRef} style={{ position: 'relative' }}>
                <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                  <input value={addInput}
                    onChange={e => { setAddInput(e.target.value.toUpperCase()); setAddError(''); setAddDropdownOpen(true); }}
                    onFocus={() => { if (addSuggestions.length) setAddDropdownOpen(true); }}
                    onKeyPress={e => e.key === 'Enter' && addTicker()}
                    placeholder="Add ticker (e.g. MSFT)"
                    style={{ flex: 1, padding: '0.5rem 0.75rem', background: 'rgba(255,255,255,0.03)',
                      border: `1px solid ${addError ? 'rgba(255,68,68,0.4)' : 'rgba(255,255,255,0.08)'}`,
                      borderRadius: 8, color: '#f8fafc', fontSize: '0.82rem', outline: 'none',
                      fontFamily: 'inherit', letterSpacing: '0.04em' }} />
                  <button onClick={addTicker} disabled={adding || !addInput}
                    style={{ padding: '0.5rem 0.85rem', background: adding || !addInput ? 'rgba(255,255,255,0.05)' : 'rgba(0,255,136,0.12)',
                      border: `1px solid ${adding || !addInput ? 'rgba(255,255,255,0.08)' : 'rgba(0,255,136,0.3)'}`,
                      borderRadius: 8, color: adding || !addInput ? '#334155' : '#00ff88',
                      fontSize: '0.78rem', fontWeight: 700, cursor: adding || !addInput ? 'not-allowed' : 'pointer',
                      fontFamily: 'inherit' }}>
                    {adding ? '…' : '+ Add'}
                  </button>
                </div>
                {/* TICKER-SEARCH-AUTOCOMPLETE — selecting a candidate calls addTicker(ticker)
                    directly (override param), avoiding the stale-closure bug that setAddInput()
                    then addTicker() would hit (React batches state updates — addTicker() would
                    otherwise still read the OLD addInput value). Same one-click-add philosophy
                    as the Home page's "Quick tickers" buttons. */}
                {addDropdownOpen && addInput.trim().length > 0 && addSuggestions.length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 40, marginTop: 4,
                    background: '#0a0f1e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8,
                    maxHeight: 220, overflowY: 'auto' }}>
                    {addSuggestions.map(s => (
                      <button key={s.ticker} type="button"
                        onClick={() => { setAddInput(s.ticker); setAddDropdownOpen(false); addTicker(s.ticker); }}
                        style={{ display: 'flex', width: '100%', justifyContent: 'space-between', gap: 8,
                          padding: '8px 10px', background: 'none', border: 'none',
                          borderBottom: '1px solid rgba(255,255,255,0.05)', cursor: 'pointer',
                          textAlign: 'left', fontFamily: 'inherit' }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,212,255,0.06)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}>
                        <span style={{ fontSize: 12, fontWeight: 800, color: '#00d4ff', flex: '0 0 55px' }}>{s.ticker}</span>
                        <span style={{ fontSize: 11, color: '#64748b', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {addError && <div style={{ fontSize: '0.68rem', color: '#ff4444', marginTop: 4 }}>{addError}</div>}
          </div>

          {/* Super Admin D3 Status — only visible to super admin */}
          {isSuperAdmin && (
            <div style={{ marginTop: '1.25rem' }}>
              <D3StatusPanel />
            </div>
          )}
        </div>

        {/* RIGHT: Movers */}
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: '1.25rem 1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <span style={{ fontSize: '0.72rem', fontWeight: 800, color: '#64748b', letterSpacing: '0.08em' }}>
              TODAY'S MOVERS
            </span>
            <button onClick={refresh}
              style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '0.3rem 0.65rem',
                background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.15)',
                borderRadius: 6, color: '#00d4ff', cursor: 'pointer', fontSize: '0.68rem',
                fontWeight: 600, fontFamily: 'inherit' }}>
              <RefreshCw size={11} /> Refresh
            </button>
          </div>
          <MoversPanel gainers={topGainers} losers={topLosers} active={mostActive}
            onNavigate={onNavigate} />
          <div style={{ marginTop: 10, fontSize: '0.65rem', color: '#475569' }}>
            Market data · 15-min delayed
          </div>
        </div>
      </div>

      {/* ── Quick analyze bar ── */}
      <div style={{ marginTop: '1.25rem', padding: '1rem 1.25rem',
        background: C.card, border: `1px solid ${C.border}`, borderRadius: 14,
        display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.72rem', color: '#475569', fontWeight: 700 }}>Quick Analyze:</span>
        {['AAPL', 'NVDA', 'TSLA', 'MSFT', 'META', 'GOOGL'].map(t => (
          <button key={t} onClick={() => onAnalyze(t)}
            style={{ padding: '0.3rem 0.7rem', background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.07)', borderRadius: 6,
              color: '#64748b', fontSize: '0.75rem', fontWeight: 700,
              cursor: 'pointer', fontFamily: 'inherit', letterSpacing: '0.04em', transition: 'all 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.color = '#00d4ff'; e.currentTarget.style.borderColor = 'rgba(0,212,255,0.3)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#64748b'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'; }}>
            {t}
          </button>
        ))}
        <span style={{ flex: 1 }} />
        <button onClick={() => onNavigate('market')}
          style={{ padding: '0.3rem 0.85rem', background: 'rgba(0,212,255,0.06)',
            border: '1px solid rgba(0,212,255,0.15)', borderRadius: 6, color: '#00d4ff',
            fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
          <Activity size={11} style={{ marginRight: 4, verticalAlign: 'middle' }} />
          Full Market Intelligence →
        </button>
      </div>

      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>
    </div>
  );
}
